import {
  createMatrixFromArray,
  applyMatrixToPoint,
  getRoomType,
  storedRoomType,
  drawLabel,
  randomColor,
} from './floorPlanUtils';
import { getAllRoomPolygons, isPointInPolygon } from './roomPolygonUtils';

/**
 * Finds all hallways in a floor plan based on room connectivity
 * @param {Object} data - The floor plan data
 * @param {Array} exteriorDoors - Array of exterior doors from findExteriorDoors
 * @returns {Array} Array of hallway rooms
 */
export const findHallways = (data, exteriorDoors, rotation) => {
  if (!data || !data.rooms || !exteriorDoors || exteriorDoors.length === 0) {
    return [];
  }

  // Build room connectivity graph
  const roomGraph = buildRoomConnectivityGraph(data);

  // Map each exterior door to the room it's connected to
  const exteriorDoorRooms = mapExteriorDoorsToRooms(
    data,
    exteriorDoors,
    rotation
  );

  // Find paths from each exterior door to all rooms
  const allPaths = {};

  exteriorDoorRooms.forEach((doorRoom) => {
    if (!doorRoom) return;

    const pathsFromDoor = findPathsFromRoom(roomGraph, doorRoom.roomId);
    allPaths[doorRoom.doorId] = pathsFromDoor;
  });

  // Identify transit rooms (potential hallways)
  const transitRooms = findTransitRooms(roomGraph, allPaths);

  // Filter transit rooms based on rules (not bathrooms, closets, etc.)
  const hallways = filterHallwaysByRoomType(data, transitRooms);

  return hallways;
};

/**
 * Builds a graph representing room connectivity through doors and openings
 * @param {Object} data - The floor plan data
 * @returns {Object} Room connectivity graph
 */
const buildRoomConnectivityGraph = (data) => {
  const graph = {};

  // Initialize graph with all rooms
  data.rooms.forEach((room) => {
    graph[room.id] = {
      room,
      connected: [],
    };
  });

  // Find all interior doors (doors that are not exterior)
  const interiorDoors = data.doors.filter((door) => {
    // Check if this door is in the list of exterior doors
    const isExterior =
      data.exteriorDoors &&
      data.exteriorDoors.some((extDoor) => extDoor.door.id === door.id);
    return !isExterior;
  });

  // Process all doors
  interiorDoors.forEach((door) => {
    const connectedRooms = findRoomsConnectedByDoor(data, door);

    if (connectedRooms.length === 2) {
      const [roomA, roomB] = connectedRooms;

      // Add connection in both directions
      if (graph[roomA]) {
        if (!graph[roomA].connected.includes(roomB)) {
          graph[roomA].connected.push(roomB);
        }
      }

      if (graph[roomB]) {
        if (!graph[roomB].connected.includes(roomA)) {
          graph[roomB].connected.push(roomA);
        }
      }
    }
  });

  // Process all openings if they exist in the data
  if (data.openings && data.openings.length > 0) {
    data.openings.forEach((opening) => {
      // Use the same connection detection as doors
      const connectedRooms = findRoomsConnectedByDoor(data, opening);

      if (connectedRooms.length === 2) {
        const [roomA, roomB] = connectedRooms;

        // Add connection in both directions
        if (graph[roomA]) {
          if (!graph[roomA].connected.includes(roomB)) {
            graph[roomA].connected.push(roomB);
          }
        }

        if (graph[roomB]) {
          if (!graph[roomB].connected.includes(roomA)) {
            graph[roomB].connected.push(roomA);
          }
        }
      }
    });
  }

  return graph;
};

/**
 * Finds which rooms are connected by a given door
 * @param {Object} data - The floor plan data
 * @param {Object} door - Door object
 * @returns {Array} Array of room IDs connected by this door
 */
const findRoomsConnectedByDoor = (data, door, rotation) => {
  // Get door transformation and properties
  const matrix = createMatrixFromArray(door.transform);
  const dimensions = door.dimensions || [1, 2, 0];
  const doorWidth = dimensions[0];

  // Get door center in world space
  const center = applyMatrixToPoint(matrix, [0, 0, 0]);

  // Get door direction from matrix
  const direction = [matrix[0], matrix[2]]; // X and Z components of first column
  const length = Math.sqrt(
    direction[0] * direction[0] + direction[1] * direction[1]
  );
  const normalizedDir = [direction[0] / length, direction[1] / length];

  // Get perpendicular direction (for checking on either side of the door)
  const perpDir = [-normalizedDir[1], normalizedDir[0]];

  // Distance to check for rooms
  const checkDistance = 0.5; // meters

  // Check points on either side of the door
  const sidePoint1 = [
    center[0] + perpDir[0] * checkDistance,
    center[2] + perpDir[1] * checkDistance,
  ];

  const sidePoint2 = [
    center[0] - perpDir[0] * checkDistance,
    center[2] - perpDir[1] * checkDistance,
  ];

  // Get all room polygons
  const roomPolygons = getAllRoomPolygons(data, rotation);

  const connectedRooms = [];

  // Check each room polygon
  Object.entries(roomPolygons).forEach(([roomId, polygon]) => {
    if (polygon && polygon.points && polygon.points.length >= 3) {
      // Check if side point 1 is in this room
      if (isPointInPolygon(sidePoint1, polygon.points)) {
        connectedRooms.push(roomId);
      }

      // Check if side point 2 is in this room
      else if (isPointInPolygon(sidePoint2, polygon.points)) {
        connectedRooms.push(roomId);
      }
    }
  });

  return connectedRooms;
};

/**
 * Maps each exterior door to the room it's connected to
 * @param {Object} data - The floor plan data
 * @param {Array} exteriorDoors - Array of exterior doors
 * @returns {Array} Array of {doorId, roomId} mappings
 */
const mapExteriorDoorsToRooms = (data, exteriorDoors, rotation) => {
  const doorRoomMap = [];
  const roomPolygons = getAllRoomPolygons(data, rotation);

  exteriorDoors.forEach((extDoor) => {
    const { door, interiorPoint } = extDoor;

    // Find which room the interior point is in
    let connectedRoomId = null;

    Object.entries(roomPolygons).forEach(([roomId, polygon]) => {
      if (polygon && polygon.points && polygon.points.length >= 3) {
        if (isPointInPolygon(interiorPoint, polygon.points)) {
          connectedRoomId = roomId;
        }
      }
    });

    if (connectedRoomId) {
      doorRoomMap.push({
        doorId: door.id,
        roomId: connectedRoomId,
      });
    }
  });

  return doorRoomMap;
};

/**
 * Finds all paths from a source room to all other rooms
 * @param {Object} roomGraph - Room connectivity graph
 * @param {String} sourceRoomId - Source room ID
 * @returns {Object} Paths from source to all other rooms
 */
const findPathsFromRoom = (roomGraph, sourceRoomId) => {
  const paths = {};
  const visited = new Set();

  // Breadth-first search to find paths
  const queue = [{ roomId: sourceRoomId, path: [sourceRoomId] }];
  visited.add(sourceRoomId);

  while (queue.length > 0) {
    const { roomId, path } = queue.shift();
    paths[roomId] = path;

    // Get connected rooms
    const neighbors = roomGraph[roomId]?.connected || [];

    for (const neighborId of neighbors) {
      if (!visited.has(neighborId)) {
        visited.add(neighborId);
        queue.push({
          roomId: neighborId,
          path: [...path, neighborId],
        });
      }
    }
  }

  return paths;
};

/**
 * Identifies transit rooms that are part of paths between other rooms
 * @param {Object} roomGraph - Room connectivity graph
 * @param {Object} allPaths - All paths from exterior doors to rooms
 * @returns {Set} Set of transit room IDs
 */
const findTransitRooms = (roomGraph, allPaths) => {
  const transitRooms = new Set();

  // For each exterior door
  Object.entries(allPaths).forEach(([doorId, pathsFromDoor]) => {
    // For each destination room
    Object.entries(pathsFromDoor).forEach(([destRoomId, path]) => {
      // Skip if path length is 1 (source room itself)
      if (path.length <= 1) return;

      // Middle rooms in the path are transit rooms
      for (let i = 0; i < path.length - 1; i++) {
        transitRooms.add(path[i]);
      }
    });
  });

  return transitRooms;
};

/**
 * Filters potential hallways based on room type rules
 * @param {Object} data - The floor plan data
 * @param {Set} transitRooms - Set of transit room IDs
 * @returns {Array} Array of hallway rooms
 */
const filterHallwaysByRoomType = (data, transitRooms) => {
  const hallways = [];

  // Check each transit room
  for (const roomId of transitRooms) {
    const room = data.rooms.find((r) => r.id === roomId);

    if (!room) continue;

    // Get room type — compared below, so as stored, never as displayed
    const roomType = (storedRoomType(room) ?? 'Unknown').toLowerCase();

    // Skip bathrooms, closets, and certain room types that can't be hallways
    const nonHallwayTypes = ['bathroom', 'closet', 'bedroom', 'master bedroom'];

    if (nonHallwayTypes.includes(roomType)) {
      continue;
    }

    // Add to hallways list
    hallways.push({
      room,
      roomId,
      roomType,
    });
  }

  return hallways;
};

/**
 * Visualizes hallways on the floor plan
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} hallways - Array of hallway rooms
 * @param {Object} options - Display options
 */
export const drawHallways = (ctx, hallways, options = {}) => {
  if (!hallways || hallways.length === 0) return;

  hallways.forEach((hallway) => {
    const { room, roomId } = hallway;

    // Check if this hallway is selected
    // const isSelected = selectedId && roomId === selectedId;
    // const color = isSelected ? '#ffaa55' : highlightColor;
    const color = 'black';

    // Get room polygon
    if (room.sections) {
      room.sections.forEach((section) => {
        if (section.center) {
          drawLabel(ctx, 'hallway', section.center[0] + 1, section.center[2], {
            fontSize: 12,
            fontFamily: 'Arial',
            color: color,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            padding: 4,
            counterRotate: true,
          });
        }
      });
    }
  });
};

/**
 * Visualizes paths from exterior doors to a specific room
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} data - The floor plan data
 * @param {Array} exteriorDoors - Array of exterior doors
 * @param {String} targetRoomId - Target room ID
 * @param {Object} options - Display options
 */
export const drawPathToRoom = (
  ctx,
  data,
  exteriorDoors,
  targetRoomId,
  rotation,
  options = {}
) => {
  if (!data || !exteriorDoors || !targetRoomId) return;

  // Build room connectivity graph
  const roomGraph = buildRoomConnectivityGraph(data);

  // Map exterior doors to rooms
  const exteriorDoorRooms = mapExteriorDoorsToRooms(
    data,
    exteriorDoors,
    rotation
  );

  // Find the shortest path from any exterior door to the target room
  let shortestPath = null;
  let shortestLength = Infinity;
  let sourceExteriorDoor = null;

  exteriorDoorRooms.forEach((doorRoom) => {
    if (!doorRoom) return;

    const pathsFromDoor = findPathsFromRoom(roomGraph, doorRoom.roomId);
    const pathToTarget = pathsFromDoor[targetRoomId];

    if (pathToTarget && pathToTarget.length < shortestLength) {
      shortestPath = pathToTarget;
      shortestLength = pathToTarget.length;
      sourceExteriorDoor = exteriorDoors.find(
        (door) => door.door.id === doorRoom.doorId
      );
    }
  });

  if (!shortestPath || !sourceExteriorDoor) return;

  // Draw the path
  const pathColor = options.pathColor || '#ff5500';
  const pathWidth = options.pathWidth || 0.2;
  const pathOpacity = options.pathOpacity || 0.7;

  // Draw a line through each room's center
  const pathPoints = [];

  // Start with the exterior door point
  pathPoints.push(sourceExteriorDoor.center);

  // Add each room's center point in the path
  shortestPath.forEach((roomId) => {
    const room = data.rooms.find((r) => r.id === roomId);
    if (!room || !room.sections || room.sections.length === 0) return;

    // Use the first section's center as the room center
    const section = room.sections[0];
    if (section.center) {
      pathPoints.push(section.center);
    }
  });

  // Draw the path
  ctx.beginPath();
  ctx.moveTo(pathPoints[0][0], pathPoints[0][2]);

  for (let i = 1; i < pathPoints.length; i++) {
    ctx.lineTo(pathPoints[i][0], pathPoints[i][2]);
  }

  ctx.strokeStyle = `${pathColor}${Math.round(pathOpacity * 255)
    .toString(16)
    .padStart(2, '0')}`;
  ctx.lineWidth = pathWidth;
  ctx.setLineDash([0.3, 0.2]);
  ctx.stroke();
  ctx.setLineDash([]);

  // Draw markers at each point
  pathPoints.forEach((point, index) => {
    ctx.beginPath();
    ctx.arc(point[0], point[2], 0.15, 0, Math.PI * 2);
    ctx.fillStyle = pathColor;
    ctx.fill();

    // Draw step number
    drawLabel(ctx, `${index + 1}`, point[0], point[2], {
      fontSize: 8,
      color: '#ffffff',
      backgroundColor: pathColor,
      padding: 2,
    });
  });

  // Add path info to the display
  if (options.showInfo !== false) {
    const roomNames = shortestPath.map((roomId) => {
      const room = data.rooms.find((r) => r.id === roomId);
      if (!room) return 'Unknown';
      return getRoomType(room, data);
    });

    const pathInfo = `Path: EXIT → ${roomNames.join(' → ')}`;

    drawLabel(
      ctx,
      pathInfo,
      20 / (ctx.getTransform().a / (window.devicePixelRatio || 1)), // Position in top-left of canvas
      20 / (ctx.getTransform().a / (window.devicePixelRatio || 1)),
      {
        fontSize: 12,
        color: '#ffffff',
        backgroundColor: pathColor,
        padding: 5,
        counterRotate: true,
      }
    );
  }
};

/**
 * Analyzes a floor plan for accessibility and room connectivity
 * @param {Object} data - The floor plan data
 * @param {Array} exteriorDoors - Array of exterior doors
 * @returns {Object} Analysis results
 */
export const analyzeFloorPlanAccessibility = (
  data,
  exteriorDoors,
  rotation
) => {
  if (!data || !data.rooms || !exteriorDoors || exteriorDoors.length === 0) {
    return {
      exteriorDoorCount: 0,
      roomCount: 0,
      hallwayCount: 0,
      accessibleRooms: [],
      inaccessibleRooms: [],
      hallways: [],
      accessStats: {},
    };
  }

  // Build room connectivity graph
  const roomGraph = buildRoomConnectivityGraph(data);

  // Map exterior doors to rooms
  const exteriorDoorRooms = mapExteriorDoorsToRooms(
    data,
    exteriorDoors,
    rotation
  );

  // Find paths from each exterior door to all rooms
  const allPaths = {};
  const accessibleRooms = new Set();

  exteriorDoorRooms.forEach((doorRoom) => {
    if (!doorRoom) return;

    const pathsFromDoor = findPathsFromRoom(roomGraph, doorRoom.roomId);
    allPaths[doorRoom.doorId] = pathsFromDoor;

    // Mark all rooms that can be reached from this door as accessible
    Object.keys(pathsFromDoor).forEach((roomId) => {
      accessibleRooms.add(roomId);
    });
  });

  // Find all hallways
  const transitRooms = findTransitRooms(roomGraph, allPaths);
  const hallways = filterHallwaysByRoomType(data, transitRooms);

  // Identify inaccessible rooms
  const inaccessibleRooms = data.rooms.filter(
    (room) => !accessibleRooms.has(room.id)
  );

  // Calculate accessibility stats
  const roomCount = data.rooms.length;
  const accessibleRoomCount = accessibleRooms.size;
  const inaccessibleRoomCount = inaccessibleRooms.length;
  const accessPercent =
    roomCount > 0 ? (accessibleRoomCount / roomCount) * 100 : 0;

  // Calculate steps from exterior to each room
  const roomAccessStats = {};

  data.rooms.forEach((room) => {
    // Skip inaccessible rooms
    if (!accessibleRooms.has(room.id)) return;

    // Find shortest path from any exterior door
    let shortestSteps = Infinity;

    exteriorDoorRooms.forEach((doorRoom) => {
      const pathsFromDoor = allPaths[doorRoom.doorId];
      const pathToRoom = pathsFromDoor[room.id];

      if (pathToRoom && pathToRoom.length < shortestSteps) {
        shortestSteps = pathToRoom.length;
      }
    });

    const roomName = getRoomType(room, data);
    roomAccessStats[room.id] = {
      roomName,
      stepsFromExterior: shortestSteps === Infinity ? -1 : shortestSteps - 1, // Subtract 1 to exclude the room itself
    };
  });

  return {
    exteriorDoorCount: exteriorDoors.length,
    roomCount,
    hallwayCount: hallways.length,
    accessibleRooms: [...accessibleRooms].map((roomId) => {
      const room = data.rooms.find((r) => r.id === roomId);
      return {
        roomId,
        roomName: room ? getRoomType(room, data) : 'Unknown',
      };
    }),
    inaccessibleRooms: inaccessibleRooms.map((room) => ({
      roomId: room.id,
      roomName: getRoomType(room, data),
    })),
    hallways: hallways.map((h) => ({
      roomId: h.roomId,
      roomName: h.roomType,
    })),
    accessStats: {
      accessiblePercent: accessPercent.toFixed(1),
      averageSteps:
        Object.values(roomAccessStats)
          .filter((s) => s.stepsFromExterior >= 0)
          .reduce((sum, s) => sum + s.stepsFromExterior, 0) /
        accessibleRoomCount,
      roomStats: roomAccessStats,
    },
  };
};
